import { Mood } from "../types/api";

// Vertical position of each mood on the trend graph's Y-axis.
// The design specifies fixed rows so the emotional scale reads intuitively:
//   top    -> positive moods (Happy, Calm)
//   middle -> Neutral
//   bottom -> negative moods (Angry, Anxious, Sad)
export type MoodRow = "top" | "middle" | "bottom";

export interface MoodConfig {
  mood: Mood;
  // Hebrew label shown in the (RTL) clinician UI.
  label: string;
  emoji: string;
  // Tailwind/CSS color used for the label, marker border and progress bar.
  color: string;
  row: MoodRow;
  // Numeric rank used by the backend (1/3/5) and as a fallback ordering value.
  rank: 1 | 3 | 5;
}

// Keyed by the mood string returned from the backend.
export const MOOD_CONFIG: Record<Mood, MoodConfig> = {
  Happy: { mood: "Happy", label: "שמח", emoji: "😊", color: "#22c55e", row: "top", rank: 5 },
  Calm: { mood: "Calm", label: "רגוע", emoji: "😌", color: "#06b6d4", row: "top", rank: 5 },
  Neutral: { mood: "Neutral", label: "ניטרלי", emoji: "😐", color: "#9ca3af", row: "middle", rank: 3 },
  Sad: { mood: "Sad", label: "עצוב", emoji: "😢", color: "#3b82f6", row: "bottom", rank: 1 },
  Anxious: { mood: "Anxious", label: "חרד", emoji: "😟", color: "#f59e0b", row: "bottom", rank: 1 },
  Angry: { mood: "Angry", label: "כועס", emoji: "😠", color: "#ef4444", row: "bottom", rank: 1 },
};

// Display order for legends and the distribution card (positive -> neutral -> negative).
export const MOOD_ORDER: Mood[] = ["Happy", "Calm", "Neutral", "Sad", "Anxious", "Angry"];

// Fixed Y positions for the trend graph. Higher value = higher on the chart.
// Moods that share the same rank sit at the same height (positive = 5,
// Neutral = 3, negative = 1).
export const MOOD_Y_POSITION: Record<Mood, number> = {
  Happy: 5,
  Calm: 5,
  Neutral: 3,
  Angry: 1,
  Anxious: 1,
  Sad: 1,
};

// Safe lookup that tolerates unexpected mood strings from the API.
export function getMoodConfig(mood: string): MoodConfig | undefined {
  return MOOD_CONFIG[mood as Mood];
}
